import isEqual from 'lodash/isEqual';
import { BooleanAttribute, getAttributesToSerialize } from '../core/attributes';
import { TimeAwareBase } from '../core/TimeAwareBase';
import { createValidator, retry } from '../libs/retry';
import {
    assertIsValidAudio,
    assertMinimum,
    assertNumber,
    assertNumberBetween,
    assertResponseOk,
    assertString,
} from '../validators';
import type { BufferingMode, LicenseEnum } from '../enums';
import type Luminary from '../';
import type { AssetInterface } from '../types';
import type { SceneGroup } from '../core/SceneGroup';
import type { Scene } from '../core/scene/Scene';
import type { Subscene } from '../core/scene/Subscene';
import { Config } from '../video-data/config';
import { PlaybackRegions, type PlaybackRegion } from '../core/PlaybackRegions';
import { equalWithinEpsilon } from '../core/asset/utils';
import { isPromiseLike } from '../common';
import { Volumes } from './types';
import type { VolumePoint } from './AudioAnimationManager';
import { QUICK_TRANSITION_DURATION } from './AudioAnimationManager';

export type AudioSourceData = {
    mp3_url: string;
    duration: number;
    source: number;
    source_id: string | null;
    license: LicenseEnum;
    attribution: string | null;
    description: string | null;
    tags: Array<string>;
    title: string | null;
    name: string;
    quality: number;
    uuid: string | null;
    id?: number;
    ai_voice: string | null;
    ai_voice_speed: number | null;
};

enum SOURCE_TYPE {
    SOURCE_DATA = 'SOURCE_DATA',
    HTML_MEDIA_ELEMENT = 'HTML_MEDIA_ELEMENT',
    URL = 'URL',
    EMPTY = 'EMPTY',
}

/**
 * The AudioAsset can be used two different ways:
 *      1. pass it a <video/> tag (or any HTMLMediaElement)
 *          - the AudioAsset creates and manages a MediaElementAudioSourceNode
 *          - in this mode, playback interaction (play/pause/seek/etc) is controlled externally, not by the AudioAsset
 *          - the AudioAsset will control volume via the #gainNode
 *      2. pass in sourceData or a URL
 *          - the AudioAsset creates and manages an AudioBufferSourceNode
 *          - in this mode, the AudioAsset controls volume as well as playback interaction
 */
export class AudioAsset extends TimeAwareBase implements AssetInterface {
    override _classType = Config.AUDIO_ASSET_TYPE;

    @BooleanAttribute()
    accessor loop = false;

    #muted = false;

    #transitionPoints: VolumePoint[] = [];

    // properties only used to control playback interaction by the AudioBufferSourceNode
    #seek = 0; // the value in seconds that we have seek'd to
    #startDelay = 0;
    #playbackRegions: Readonly<Readonly<PlaybackRegion>[]> = [];

    #volume = Volumes.FULL_VOLUME;

    #isLoaded = false;
    #failedToLoad = false;
    #isBufferLoadFinished = false;

    #url: string = undefined;
    #buffer: AudioBuffer = undefined;
    // array of video elements per playback region - maps to mediaAsset._playbackRegionSources
    #htmlElements: HTMLMediaElement[] = [];
    // see #createSafeMediaElementSource() - map of audio source nodes from video elements
    #connectedMediaElements = new WeakMap<HTMLMediaElement, MediaElementAudioSourceNode>();
    #mediaElementAudioSourceNodes: MediaElementAudioSourceNode[] = [];
    #bufferSourceNodes: AudioBufferSourceNode[] = [];
    #gainNode: GainNode = undefined;
    #playbackRegionGainNodes: GainNode[] = [];
    #loadingSourceUrl: string = undefined;
    #sentCanPlay: boolean = false;

    #sourceType = SOURCE_TYPE.EMPTY;

    // the data from sourceData
    #mp3Url: string = undefined;
    #sourceDurationSeconds: number = 0;
    #source: number = undefined;
    #sourceId: string = undefined;
    #license: LicenseEnum = undefined;
    #attribution: string = undefined;
    #description: string = undefined;
    #tags: Array<string> = undefined;
    #title: string = undefined;
    #name: string = undefined;
    #quality: number = undefined;
    #sourceDataId: number = undefined;
    #sourceDataUuid: string = undefined;
    #ai_voice: string = undefined;
    #ai_voice_speed: number = undefined;

    override destroy() {
        this.stop();

        super.destroy();
        if (this.#gainNode) {
            this.#gainNode.disconnect();
            this.#gainNode = undefined;
        }
        if (this.#playbackRegionGainNodes.length) {
            this.#playbackRegionGainNodes.forEach((node) => {
                node.disconnect();
            });
            this.#playbackRegionGainNodes = [];
        }
        if (this.#bufferSourceNodes.length) {
            this.#bufferSourceNodes.forEach((node) => {
                node.disconnect();
            });
            this.#bufferSourceNodes = [];
        }
        if (this.#mediaElementAudioSourceNodes.length > 0) {
            this.#mediaElementAudioSourceNodes.forEach((node) => {
                node.disconnect();
            });
            this.#mediaElementAudioSourceNodes = [];
        }
    }

    get controlsPlayback() {
        return this.#sourceType === SOURCE_TYPE.URL || this.#sourceType === SOURCE_TYPE.SOURCE_DATA;
    }

    get failedToLoad() {
        return this.#failedToLoad;
    }

    override get isLoaded() {
        if (this.#sourceType === SOURCE_TYPE.HTML_MEDIA_ELEMENT) {
            return this.#htmlElements.every(
                (videoElement) => videoElement && videoElement.duration !== undefined,
            );
        }
        return this.#isLoaded || !this._shouldLoad;
    }

    override get isDoneTryingToLoad() {
        return this.isLoaded || this.failedToLoad;
    }

    get muted() {
        return this.#muted;
    }

    set muted(muted) {
        this.#muted = muted;
    }

    set transitionPoints(transitionPoints) {
        this.#transitionPoints = transitionPoints;
    }

    get transitionPoints() {
        return this.#transitionPoints;
    }

    /**
     * @deprecated use {@link sourceData} instead
     */
    get source(): AudioSourceData {
        return this.sourceData;
    }

    /**
     * @deprecated use {@link sourceData} instead
     */
    set source(data: AudioSourceData) {
        this.sourceData = data;
    }

    get sourceData(): AudioSourceData {
        if (this.#sourceType === SOURCE_TYPE.EMPTY) {
            return null;
        }
        return {
            mp3_url: this.#mp3Url,
            duration: this.#sourceDurationSeconds,
            source: this.#source,
            source_id: this.#sourceId,
            license: this.#license,
            attribution: this.#attribution,
            description: this.#description,
            tags: this.#tags,
            title: this.#title,
            name: this.#name,
            quality: this.#quality,
            uuid: this.#sourceDataUuid,
            id: this.#sourceDataId,
            ai_voice: this.#ai_voice ?? null,
            ai_voice_speed: this.#ai_voice_speed ?? null,
        };
    }
    set sourceData(data: AudioSourceData) {
        if (isEqual(this.sourceData, data)) {
            return;
        }

        assertIsValidAudio(data, true);

        this.#updateSourceData(data);

        this.#playbackRegions = [[0, this.sourceDuration]];
    }

    #updateSourceData(sourceData: AudioSourceData) {
        this.#mp3Url = sourceData.mp3_url;
        this.#sourceDurationSeconds = sourceData.duration;
        this.#source = sourceData.source;
        this.#sourceId = sourceData.source_id;
        this.#license = sourceData.license;
        this.#attribution = sourceData.attribution;
        this.#description = sourceData.description;
        this.#tags = sourceData.tags;
        this.#title = sourceData.title;
        this.#name = sourceData.name;
        this.#quality = sourceData.quality;
        this.#sourceDataId = sourceData.id;
        this.#sourceDataUuid = sourceData.uuid;
        this.#ai_voice = sourceData.ai_voice;
        this.#ai_voice_speed = sourceData.ai_voice_speed;

        // set isLoaded and sourceType
        this.#isLoaded = false;
        this.#sourceType = SOURCE_TYPE.SOURCE_DATA;

        // clear the other "source" properties
        this.#url = undefined;
        this.#htmlElements = [];

        this.requiresUpdate = true;
    }

    /**
     * Used to change the source data of the media asset without resetting properties like volumne, trim, and transformations.
     * The new source must have the same visual piece of media
     *
     * Use cases: audio enhancement
     */
    setSourceDataWithoutResettingProperties(data: AudioSourceData) {
        if (isEqual(this.sourceData, data)) {
            return;
        }

        assertIsValidAudio(data, true);

        this.#updateSourceData(data);
    }

    /**
     * @internal
     */
    get url() {
        return this.#url;
    }

    /**
     * @internal
     */
    set url(url: string) {
        if (this.#url === url) {
            return;
        }
        assertString(url);
        this.#url = url;

        // set isLoaded and sourceType
        this.#sourceType = SOURCE_TYPE.URL;
        this.#isLoaded = false;

        // clear the other "source" properties
        this.#mp3Url = undefined;
        this.#sourceDurationSeconds = 0;
        this.#source = undefined;
        this.#sourceId = undefined;
        this.#license = undefined;
        this.#attribution = undefined;
        this.#description = undefined;
        this.#tags = undefined;
        this.#title = undefined;
        this.#name = undefined;
        this.#quality = undefined;
        this.#sourceDataId = undefined;
        this.#htmlElements = [];
        this.#sourceDataUuid = undefined;
        this.#ai_voice = undefined;
        this.#ai_voice_speed = undefined;
        this.#playbackRegions = [];

        this.requiresUpdate = true;
    }

    /**
     * @internal
     */
    get mediaElements() {
        return this.#htmlElements;
    }

    /**
     * @internal
     */
    set mediaElements(sources: HTMLMediaElement[]) {
        if (isEqual(this.#htmlElements, sources)) {
            return;
        }
        this.#htmlElements = sources;

        // set isLoaded and sourceType
        this.#isLoaded = true; // in this case, we are using a <video/> tag, and loading is handled by the MediaAsset.
        this.#sourceType = SOURCE_TYPE.HTML_MEDIA_ELEMENT;

        // clear the other "source" properties
        this.#mp3Url = undefined;
        this.#sourceDurationSeconds = 0;
        this.#source = undefined;
        this.#sourceId = undefined;
        this.#license = undefined;
        this.#attribution = undefined;
        this.#description = undefined;
        this.#tags = undefined;
        this.#title = undefined;
        this.#name = undefined;
        this.#quality = undefined;
        this.#sourceDataId = undefined;
        this.#sourceDataUuid = undefined;
        this.#url = undefined;
        this.#ai_voice = undefined;
        this.#ai_voice_speed = undefined;

        this.requiresUpdate = true;
    }

    /**
     * With multiple playback regions mediaAsset creates multiple video elements, and we need to create
     * multiple MediaElementAudioSourceNodes for each video element. AudioContext doesn't allow creating
     * an audio source node with a video element that is already connected to the audio context, so
     * this method will return the audio source node if it's already created, or create a new one and
     * store the reference of it in a map where the key is the video element.
     */
    #createSafeMediaElementSource(videoElement: HTMLMediaElement) {
        if (this.#connectedMediaElements.has(videoElement)) {
            return this.#connectedMediaElements.get(videoElement);
        }

        if (!(this.root.audioContext instanceof AudioContext)) {
            throw new Error('Cannot create MediaElementAudioSourceNode without an AudioContext');
        }

        const sourceNode = this.root.audioContext.createMediaElementSource(videoElement);
        this.#connectedMediaElements.set(videoElement, sourceNode);

        return sourceNode;
    }

    override _propagatePlay() {
        this.#updateHTMLMuted();
        if (!this.isLoaded || !this._shouldLoad) {
            return;
        }

        this._constructAudioGraph();
        this._buildGainCurve();
        this._buildPlaybackRegionGainCurve();

        if (!this.controlsPlayback) {
            return;
        }

        // you can only play an AudioBufferSourceNode one time. The official recommendation is that
        // the audio graph is cheap to construct, and should be re-built on every call to play:
        // https://developer.mozilla.org/en-US/docs/Web/API/AudioBufferSourceNode
        this._schedulePlayback();
    }

    override _propagatePause() {
        if (!this.isLoaded) {
            return;
        }

        if (!this.controlsPlayback) {
            return;
        }

        // you can only play an AudioBufferSourceNode one time. So we need to stop playback immediately.
        // if/when we call play() again, we will re-create the bufferSourceNode and re-setup the correct
        // playback state
        if (this.#bufferSourceNodes.length) {
            this.#bufferSourceNodes.forEach((node) => {
                try {
                    node.stop();
                } catch (error) {
                    // do nothing
                }
            });
        }
    }

    stop() {
        // since we don't keep track of the "currentTime" in the AudioAsset
        // stop is no different than pause!
        this._propagatePause();
    }

    /**
     * The startDelay is an amount of time (in ms) that the audio asset should wait before playing.
     * This is useful for situations when you want to be able to schedule playback to begin at
     * some specific time in the future. For example, when we render the audio, we want to schedule scene-based
     * audio to start playing when the scene first appears on the scene.
     *
     * This property is not serialized, it's only used to schedule playback.
     */
    get startDelay() {
        return this.#startDelay;
    }

    set startDelay(startDelay) {
        assertNumberBetween(startDelay, -Infinity, Infinity);
        this.#startDelay = startDelay;
    }

    /**
     * When scheduling playback, audio assets earlier in the timeline have a seek time of 0
     * so seek needs to be adjusted to account for the startDelay
     */
    #calculateSeekForPlayback() {
        let updatedStartDelay = this.#startDelay;
        let seekWithStartDelay = this.#seek;
        if (this.#startDelay < 0) {
            // if the startDelay is negative, that means the track
            // should have started in the past. That's the same thing
            // as seeking into the future!
            seekWithStartDelay = this.#seek - this.#startDelay;
            updatedStartDelay = 0;
        }
        return {
            seekWithStartDelay,
            updatedStartDelay,
        };
    }

    /**
     * The startTime is a number of milliseconds that represents an offset into the audio asset
     * from which the audio asset should always play. For example, if we have an audio asset, but
     * we wish to skip the first 5 seconds of the track when we tell it to play, we would set
     * the startTime to 5000.
     */
    get startTime(): number {
        return PlaybackRegions.getStartTime(this.playbackRegions);
    }

    set startTime(startTime: number) {
        const playbackRegionStartTime = PlaybackRegions.getStartTime(this.playbackRegions);
        if (playbackRegionStartTime === startTime) {
            return;
        }
        assertNumberBetween(startTime, 0, Infinity);

        this.#playbackRegions = PlaybackRegions.setStartTime(
            startTime,
            this.playbackRegions,
            this.sourceDuration,
        );
        this.requiresUpdate = true;
    }

    /**
     * The endTime is in milliseconds. It represents the time at which the audio asset should stop playing.
     */
    get endTime(): number {
        const playbackRegionEndTime = PlaybackRegions.getEndTime(this.playbackRegions);
        if (playbackRegionEndTime === null) {
            return this.sourceDuration;
        }
        return playbackRegionEndTime;
    }

    set endTime(endTime: number) {
        // audio asset's duration is in seconds, so we need to convert it to milliseconds
        // but in the conversion we risk floating point comparison errors, so we need to round it to a precision

        if (endTime === null) {
            endTime = this.sourceDuration;
        }
        // cap the end time to the duration
        if (endTime > this.sourceDuration) {
            endTime = this.sourceDuration;
        }
        const playbackRegionEndTime = PlaybackRegions.getEndTime(this.playbackRegions);
        if (equalWithinEpsilon(endTime, playbackRegionEndTime)) {
            return;
        }
        assertNumber(endTime, true);
        assertMinimum(endTime, 0, true);

        this.#playbackRegions = PlaybackRegions.setEndTime(
            endTime,
            this.playbackRegions,
            this.sourceDuration,
        );
        this.requiresUpdate = true;
    }

    _playbackRegionsCustomLoad(value: PlaybackRegion[]) {
        this.#playbackRegions = value;
        this.requiresUpdate = true;
    }

    get playbackRegions(): Readonly<Readonly<PlaybackRegion>[]> {
        return this.#playbackRegions;
    }

    cut(start: number, end: number) {
        if (!this.controlsPlayback) {
            throw new Error('Cut is not supported for AudioAssets that do not control playback');
        }

        this.#playbackRegions = PlaybackRegions.cut(
            start,
            end,
            this.sourceDuration,
            this.#playbackRegions,
        );
        this.requiresUpdate = true;
    }

    uncut(start: number, end: number) {
        if (!this.controlsPlayback) {
            throw new Error('Uncut is not supported for AudioAssets that do not control playback');
        }

        this.#playbackRegions = PlaybackRegions.uncut(
            start,
            end,
            this.sourceDuration,
            this.#playbackRegions,
        );
        this.requiresUpdate = true;
    }

    get volume() {
        return this.#volume;
    }

    set volume(newVolume: number) {
        if (newVolume === this.#volume) {
            return;
        }
        assertNumberBetween(newVolume, Volumes.SILENT, Volumes.FULL_VOLUME);
        this.#volume = newVolume;
        this.requiresUpdate = true;
    }

    /**
     * Duration after cuts are applied, in milliseconds
     */
    get duration() {
        return PlaybackRegions.getDurationFromPlaybackRegions(this.#playbackRegions);
    }

    /**
     * This is the duration of the audio file in MS. The AudioAsset may actually play for:
     *  - longer than the duration (if it loops)
     *  - shorter than the duration (if only part of it plays)
     */
    get sourceDuration() {
        return this.#sourceDurationSeconds * 1000.0;
    }

    async _bufferAtTime(time: number, _mode: BufferingMode) {
        this.#seek = time;
    }

    override _update() {
        void this.loadSource();
    }

    #transitionPointIsInPresentOrFuture(transitionPoint: VolumePoint) {
        return this.#getTransitionPointTimeFromNow(transitionPoint) >= 0;
    }

    #getTransitionPointTimeFromNow(transitionPoint: VolumePoint) {
        return this.#startDelay + transitionPoint.time - this.#seek;
    }

    /**
     * @internal
     */
    _getAssetTimeFromPlaybackTime(playbackTime: number) {
        const assetTime = PlaybackRegions.playbackTimeToAssetTime(
            this.#playbackRegions,
            this.sourceDuration,
            playbackTime,
        );
        return this.#startDelay + assetTime;
    }

    /**
     * @internal
     */
    _getPlaybackTimeFromSeek(assetTime: number) {
        const playbackTime = PlaybackRegions.assetTimeToPlaybackTime(
            this.#playbackRegions,
            this.sourceDuration,
            assetTime,
        );
        return this.#startDelay + playbackTime - this.#seek;
    }

    /**
     * The gain node controls the volume (and mute) of the audio. However, to enable automatic playback
     * of muted videos, we need to make sure the muted property of the HTMLMediaElement is set to true.
     * Otherwise, if the user hasn't interacted with the page, the video won't play.
     * https://developer.chrome.com/blog/autoplay/#new-behaviors
     */
    #updateHTMLMuted() {
        if (this.#htmlElements.length === 0) {
            return;
        }
        const muted =
            this.#muted ||
            this.root.muted ||
            this.#transitionPoints.length === 0 ||
            this.volume === Volumes.SILENT;
        // mute all playback regions
        this.#htmlElements.forEach((videoSource) => {
            videoSource.muted = muted;
        });
    }

    reschedulePlaybackRegionGainCurve(assetTime: number) {
        if (this.#playbackRegionGainNodes.length !== this.#playbackRegions.length) {
            // we have not yet built the gain curve - that will happen on the call to play
            return;
        }
        const seekTime = PlaybackRegions.assetTimeToPlaybackTime(
            this.#playbackRegions,
            this.sourceDuration,
            assetTime,
        );
        this.#seek = seekTime;
        this._buildPlaybackRegionGainCurve();
    }

    /**
     * Build the gain curve for every play back region
     * Volume is ducked at the boundaries of playback regions (on the cut) to reduce audio popping
     */
    _buildPlaybackRegionGainCurve() {
        // cancel all curves going forward
        this.#playbackRegionGainNodes.forEach((node) => {
            node.gain.cancelScheduledValues(this.root.audioContext.currentTime);
        });

        // When looping, set volume and do not schedule any gain events for the playback region. The main gain node will handle any volume changes
        if (this.loop) {
            this.#playbackRegionGainNodes.forEach((playbackRegionGainNode) => {
                playbackRegionGainNode.gain.value = 1;
            });
            return;
        }

        const { seekWithStartDelay } = this.#calculateSeekForPlayback();

        for (let i = 0; i < this.#playbackRegions.length; i++) {
            if (
                PlaybackRegions.playbackTimeIsInFuturePlaybackRegion(
                    this.#playbackRegions,
                    this.sourceDuration,
                    seekWithStartDelay,
                    i,
                )
            ) {
                // The seek time is after this playback region so it does not get played (stays muted). Skip it
                continue;
            }

            const [startTime, endTime] = this.#playbackRegions[i];
            const playbackRegionGainNode = this.#playbackRegionGainNodes[i];

            // Ramp up at the start of the playback region
            const rampUpStartTime = this._getPlaybackTimeFromSeek(startTime);
            if (rampUpStartTime < 0) {
                // Playback started in the past so immediately transition to full volume
                playbackRegionGainNode.gain.linearRampToValueAtTime(
                    1,
                    this.root.audioContext.currentTime,
                );
            } else {
                playbackRegionGainNode.gain.linearRampToValueAtTime(
                    0,
                    this.root.audioContext.currentTime + rampUpStartTime / 1000.0,
                );
                playbackRegionGainNode.gain.linearRampToValueAtTime(
                    1.0,
                    this.root.audioContext.currentTime +
                        (rampUpStartTime + QUICK_TRANSITION_DURATION) / 1000.0,
                );
            }

            // Ramp down at the end of the playback region
            playbackRegionGainNode.gain.linearRampToValueAtTime(
                1.0,
                this.root.audioContext.currentTime +
                    Math.max(
                        0,
                        this._getPlaybackTimeFromSeek(endTime) - QUICK_TRANSITION_DURATION,
                    ) /
                        1000.0,
            );
            playbackRegionGainNode.gain.linearRampToValueAtTime(
                0,
                this.root.audioContext.currentTime +
                    this._getPlaybackTimeFromSeek(endTime) / 1000.0,
            );
        }
    }

    _buildGainCurve() {
        // cancel all curves going forward
        this.#gainNode.gain.cancelScheduledValues(this.root.audioContext.currentTime);
        if (this.#muted || this.root.muted || this.#transitionPoints.length === 0) {
            return;
        }

        let firstFuturePointIndex = 0;
        for (let i = 0; i < this.#transitionPoints.length; i++) {
            const currentPoint = this.#transitionPoints[i];
            if (this.#transitionPointIsInPresentOrFuture(currentPoint)) {
                // we found the first point that is actually in the future!
                // Store it and break.
                firstFuturePointIndex = i;
                break;
            }
        }
        if (firstFuturePointIndex > 0) {
            // we are skipping some points in the beginning, so we need to do some linear
            // interpolation to get the value at the beginning of the first transition
            const firstFuturePoint = this.#transitionPoints[firstFuturePointIndex];
            const lastHistoricPoint = this.#transitionPoints[firstFuturePointIndex - 1];

            // linear interpolation: https://matthew-brett.github.io/teaching/linear_interpolation.html
            const y0 = firstFuturePoint.volume;
            const y1 = lastHistoricPoint.volume;
            const x0 = this.#getTransitionPointTimeFromNow(firstFuturePoint);
            const x1 = this.#getTransitionPointTimeFromNow(lastHistoricPoint);
            const x = 0; // interpolating for time zero
            const y = y0 + ((x - x0) * (y1 - y0)) / (x1 - x0);

            this.#gainNode.gain.linearRampToValueAtTime(
                y / 100.0,
                this.root.audioContext.currentTime, // setting the volume at exactly the currentTime (time zero)
            );
        } else {
            // check if ALL the transition points are in the past. If so,
            // use the last point as the current volume
            if (this.transitionPoints.length > 0) {
                let lastPoint = this.transitionPoints[0];
                this.#transitionPoints.forEach((transitionPoint) => {
                    if (transitionPoint.time > lastPoint.time) {
                        lastPoint = transitionPoint;
                    }
                });
                if (this.#getTransitionPointTimeFromNow(lastPoint) < 0) {
                    this.#gainNode.gain.linearRampToValueAtTime(
                        lastPoint.volume / 100.0,
                        this.root.audioContext.currentTime, // setting the volume at exactly the currentTime (time zero)
                    );
                }
            }
        }

        // now the simple part - just add the future points to the curve!
        for (let i = firstFuturePointIndex; i < this.#transitionPoints.length; i++) {
            const currentPoint = this.#transitionPoints[i];
            if (this.#transitionPointIsInPresentOrFuture(currentPoint)) {
                this.#gainNode.gain.linearRampToValueAtTime(
                    currentPoint.volume / 100.0,
                    this.root.audioContext.currentTime +
                        this.#getTransitionPointTimeFromNow(currentPoint) / 1000.0,
                );
            }
        }
    }

    _schedulePlayback() {
        // only the AudioBufferSourceNode is controlled by the AudioAsset.
        // if the audio is coming off of an htmlMediaElement, playback is controlled by the MediaAsset
        if (!this.controlsPlayback) {
            return;
        }

        const now = this.root.audioContext.currentTime;
        const { seekWithStartDelay, updatedStartDelay } = this.#calculateSeekForPlayback();

        if (this.loop) {
            this.#scheduleLoopingPlayback(now, seekWithStartDelay, updatedStartDelay);
        } else {
            this.#scheduleNonLoopingPlayback(now, seekWithStartDelay, updatedStartDelay);
        }
    }

    /**
     * Schedule each playback region back-to-back. Regions whose end is already behind the seek
     * are skipped; the region that contains the seek starts mid-region at the seek offset.
     */
    #scheduleNonLoopingPlayback(
        now: number,
        seekWithStartDelay: number,
        updatedStartDelay: number,
    ) {
        let durationPlayedSeconds = 0;
        this.#bufferSourceNodes.forEach((node, i) => {
            const [regionStartTime, regionEndTime] = this.#playbackRegions[i];

            let offset = 0;
            let duration = 0;
            if (
                PlaybackRegions.playbackTimeIsInFuturePlaybackRegion(
                    this.#playbackRegions,
                    this.sourceDuration,
                    seekWithStartDelay,
                    i,
                )
            ) {
                // The seek time is after this playback region so it does not get played. Skip it
                return;
            }
            // Start playing the current region when the previous playback region is done
            const when = now + updatedStartDelay / 1000.0 + durationPlayedSeconds;
            if (
                PlaybackRegions.playbackTimeIsInPlaybackRegion(
                    this.#playbackRegions,
                    this.sourceDuration,
                    seekWithStartDelay,
                    i,
                )
            ) {
                // Begin at the seek time and play the remaining duration of the current region
                const seekInAssetTime = PlaybackRegions.playbackTimeToAssetTime(
                    this.#playbackRegions,
                    this.sourceDuration,
                    seekWithStartDelay,
                );
                offset = seekInAssetTime / 1000.0;
                duration = (regionEndTime - seekInAssetTime) / 1000.0;
            } else {
                // Begin at the start of the region and play until the end of the region
                offset = regionStartTime / 1000.0;
                duration = (regionEndTime - regionStartTime) / 1000.0;
            }

            node.start(when, offset, duration);
            durationPlayedSeconds += duration;
        });
    }

    /**
     * For looping assets the seek time can exceed the loop region's duration (e.g. seeking into a
     * 3-minute video when the background music is only 30 seconds). Wrap the seek into the loop
     * region so the buffer source is still scheduled instead of being silently skipped as a
     * "future" region.
     */
    #scheduleLoopingPlayback(now: number, seekWithStartDelay: number, updatedStartDelay: number) {
        if (this.#playbackRegions.length > 1) {
            throw new Error('Looping is not supported with multiple playback regions');
        }
        const [node] = this.#bufferSourceNodes;
        const [regionStartTime, regionEndTime] = this.#playbackRegions[0];
        const playbackDurationMS = regionEndTime - regionStartTime;
        assertMinimum(seekWithStartDelay, 0);
        const wrappedPlaybackTimeMS =
            playbackDurationMS > 0 ? seekWithStartDelay % playbackDurationMS : 0;
        const offsetMS = regionStartTime + wrappedPlaybackTimeMS;

        node.loop = true;
        node.loopStart = this.startTime / 1000.0;
        node.loopEnd = this.endTime / 1000.0;
        // Do not pass in duration when looping because when duration is set, playback is stopped
        // manually via stop().
        node.start(now + updatedStartDelay / 1000.0, offsetMS / 1000.0);
    }

    #createGainNode() {
        if (this.#gainNode) {
            this.#gainNode.disconnect();
        }
        return this.root.audioContext.createGain();
    }

    #createPlaybackRegionGainNodes() {
        this.#playbackRegionGainNodes.forEach((node) => {
            node.disconnect();
        });
        return this.playbackRegions.map(() => {
            return this.root.audioContext.createGain();
        });
    }

    _constructAudioGraph() {
        if (
            this.#sourceType === SOURCE_TYPE.HTML_MEDIA_ELEMENT &&
            // if there are any video elements that are not connected to the audio context, we need to create the audio source nodes
            this.#htmlElements.some((videoSource) => !this.#connectedMediaElements.has(videoSource))
        ) {
            this.#mediaElementAudioSourceNodes.forEach((node) => {
                node.disconnect();
            });

            if (!(this.root.audioContext instanceof AudioContext)) {
                throw new Error('media element source type is only supported with AudioContext.');
            }

            this.#gainNode = this.#createGainNode();
            this.#playbackRegionGainNodes = this.#createPlaybackRegionGainNodes();
            // Create an audio source node and gain node for every video element
            this.#mediaElementAudioSourceNodes = this.#htmlElements.map((videoSource, i) => {
                const audioSourceNode = this.#createSafeMediaElementSource(videoSource);
                const playbackRegionGainNode = this.#playbackRegionGainNodes[i];
                audioSourceNode.connect(playbackRegionGainNode);
                playbackRegionGainNode.connect(this.#gainNode);
                playbackRegionGainNode.gain.value = 0; // Start muted. If it needs to be audible, the gain curve sets volume ramps
                return audioSourceNode;
            });

            this.#gainNode.connect(this.root.audioContext.destination);
        } else if (
            this.#sourceType === SOURCE_TYPE.URL ||
            this.#sourceType === SOURCE_TYPE.SOURCE_DATA
        ) {
            this.#bufferSourceNodes.forEach((node) => {
                node.disconnect();
            });
            this.#gainNode = this.#createGainNode();
            this.#playbackRegionGainNodes = this.#createPlaybackRegionGainNodes();
            // Create an audio source node and gain node for every playback region
            this.#bufferSourceNodes = this.#playbackRegions.map((_, i) => {
                const sourceNode = this.root.audioContext.createBufferSource();
                sourceNode.buffer = this.#buffer;
                const playbackRegionGainNode = this.#playbackRegionGainNodes[i];
                sourceNode.connect(playbackRegionGainNode);
                playbackRegionGainNode.connect(this.#gainNode);
                playbackRegionGainNode.gain.value = 0; // Start muted. If it needs to be audible, the gain curve sets volume ramps
                return sourceNode;
            });
            this.#gainNode.connect(this.root.audioContext.destination);
        }

        // By default, mute the audio.
        // If it needs to audible, the audio animation curves will set the volume.
        this.#gainNode.gain.value = 0;
    }

    onLoaded() {
        if (!this.isLoaded) {
            this._emitAssetCanPlay();
            return;
        }
        const originalDuration = this.#sourceDurationSeconds;

        this.#sourceDurationSeconds = this.#buffer.duration;
        // set the playback regions if they're not already set, this happens from set url or set mediaElement
        if (this.playbackRegions.length === 0) {
            this.#playbackRegions = [[0, this.sourceDuration]];
        }

        // if the loaded duration is shorter than the end time, we need to cap it
        if (this.endTime > this.sourceDuration) {
            // cap the end of the playback region to sourceDuration
            this.#playbackRegions = PlaybackRegions.cut(
                this.sourceDuration,
                this.endTime,
                this.endTime,
                this.playbackRegions,
            );
        } else if (
            this.endTime < this.sourceDuration &&
            equalWithinEpsilon(this.endTime, originalDuration * 1000, 1)
        ) {
            // The sourceDuration can change slightly (e.g. after luminary loads the asset, when source data is replaced by audio enhancement)
            // If the end time is less than and close to the original source duration, we know the user has not made a cut,
            // so we need to update the PlaybackRegions to include the whole duration
            this.#playbackRegions = PlaybackRegions.setEndTime(
                this.sourceDuration,
                this.playbackRegions,
                this.sourceDuration,
            );
        }

        this.requiresUpdate = true;
        this._emitAssetCanPlay();
    }

    _emitAssetCanPlay() {
        if (!this.#sentCanPlay) {
            this.#sentCanPlay = true;
            this.emit('assetCanPlay');
        }
    }

    /**
     * Because AudioAsset extends TimeAwareBase, we need to implement _emitAssetsCanPlay, but
     * this is a single asset so we emit assetCanPlay. Conceptually, this should be equivalent to
     * _emitAssetCanPlay in BaseAsset. This bubbles the assetCanPlay event from onLoaded.
     */
    _emitAssetsCanPlay() {
        if (this.luminaryParent) {
            this.luminaryParent.emit('assetCanPlay');
        }
    }

    get luminaryParent(): Luminary | SceneGroup | Scene | Subscene {
        // If the parent is another asset, use that asset's luminary parent.
        if (Config.isAsset(this.parent)) {
            return this.parent.luminaryParent;
        }

        if (this.parent === this.root) {
            return this.root;
        }

        return this.parent as SceneGroup;
    }

    isLoadingUrl(url: string): boolean {
        return !!this.#loadingSourceUrl && this.#loadingSourceUrl === url;
    }

    get sourceType() {
        return this.#sourceType;
    }

    /**
     * @deprecated use _shouldLoad instead.
     */
    get shouldLoad(): boolean {
        return this._shouldLoad;
    }

    get _shouldLoad(): boolean {
        if (this.#volume === Volumes.SILENT) {
            return false;
        }
        if (this.#sourceType === SOURCE_TYPE.EMPTY) {
            return false;
        }
        if (typeof this.fetchResources === 'boolean') {
            return this.fetchResources;
        }
        return Boolean(
            this.fetchResources && this.fetchResources.audio && this.sourceData !== null,
        );
    }

    override get canPlay() {
        return this.isDoneTryingToLoad;
    }

    static #cache: Map<string, AudioBuffer | Promise<AudioBuffer>> = new Map();

    static clearCache() {
        AudioAsset.#cache.clear();
    }

    async loadSource() {
        if (this.isDoneTryingToLoad || this.#sourceType === SOURCE_TYPE.HTML_MEDIA_ELEMENT) {
            return;
        }

        if (this.#sourceType === SOURCE_TYPE.URL || this.#sourceType === SOURCE_TYPE.SOURCE_DATA) {
            const urlToLoad = this.#url || this.#mp3Url;
            let cacheEntry = AudioAsset.#cache.get(urlToLoad);

            if (cacheEntry) {
                if (isPromiseLike(cacheEntry)) {
                    cacheEntry = await cacheEntry;
                }
                this.#buffer = cacheEntry;
                this.#isLoaded = true;
                this.#isBufferLoadFinished = true;
                this.emit('audioBufferReady');
                this.#loadingSourceUrl = undefined;
                this.onLoaded();
                return;
            }

            if (this.isLoadingUrl(urlToLoad)) {
                // we've already started loading this url, just wait!
                return;
            }
            this.#sentCanPlay = false;
            this.#loadingSourceUrl = urlToLoad;

            let resolveCache: (b: AudioBuffer) => void;
            let rejectCache: (e: unknown) => void;
            const deferred = new Promise<AudioBuffer>((resolve, reject) => {
                resolveCache = resolve;
                rejectCache = reject;
            });
            AudioAsset.#cache.set(urlToLoad, deferred);

            this.root._assetNetworkManager.waitForNetworkSlot(this, (requestFinished) => {
                const validator = createValidator(assertResponseOk);
                const fetchAndDecodeAudio = async () => {
                    const response = await retry(5, validator(fetch), this.#loadingSourceUrl);
                    const arrayBuffer = await response.arrayBuffer();
                    return this.root.audioContext.decodeAudioData(arrayBuffer);
                };
                fetchAndDecodeAudio()
                    .then((buffer) => {
                        if (this.#loadingSourceUrl !== urlToLoad) {
                            return;
                        }
                        this.#buffer = buffer;
                        this.#isLoaded = true;
                        this.#isBufferLoadFinished = true;
                        this.emit('audioBufferReady');
                        AudioAsset.#cache.set(urlToLoad, buffer);
                        resolveCache(buffer);
                    })
                    .catch((err) => {
                        if (this.#loadingSourceUrl !== urlToLoad) {
                            return;
                        }
                        console.warn(err);
                        this.#failedToLoad = true;
                        this.#isBufferLoadFinished = true;
                        this.emit('audioBufferReady');
                        rejectCache(err);
                    })
                    .finally(() => {
                        this.#isBufferLoadFinished = true;
                        this.emit('audioBufferReady');
                        requestFinished();
                        if (this.#loadingSourceUrl !== urlToLoad) {
                            return;
                        }
                        this.#loadingSourceUrl = undefined;
                        this.onLoaded();
                    });
            });
        }
    }

    async waitForAudioBuffer() {
        if (this.#isBufferLoadFinished || !this._shouldLoad) {
            return;
        }
        await new Promise<void>((resolve) => {
            this.once('audioBufferReady', () => resolve());
        });
    }

    override get propertiesToSerialize(): string[] {
        return super.propertiesToSerialize
            .concat(['sourceData', 'id', 'playbackRegions'])
            .concat(getAttributesToSerialize(this));
    }

    override get editableProperties() {
        return super.editableProperties.concat(['volume']);
    }

    override get readonlyProperties(): string[] {
        return super.readonlyProperties.concat(['id']);
    }

    _sourceDataCustomSave() {
        if (
            this.#sourceType !== SOURCE_TYPE.SOURCE_DATA &&
            this.#sourceType !== SOURCE_TYPE.EMPTY
        ) {
            throw Error('you can only serialize AudioAssets that have source set with SOURCE_DATA');
        }
        return this.source;
    }

    /**
     * __sourceNodes is only intended to be used by tests.
     * @internal
     */
    get __sourceNodes() {
        return this.#bufferSourceNodes || this.#mediaElementAudioSourceNodes;
    }

    /**
     * __gainNode is only intended to be used by tests.
     * @internal
     */
    get __gainNode() {
        return this.#gainNode;
    }

    getLoudnessAndPeakMixAtTime(
        time: number,
        loudnessPercent = 0.9,
        gain = 1.25,
        windowSize = 20,
    ): number {
        if (!this.#buffer) {
            console.log('Buffer not found.');
            return 0;
        }

        const sampleRate = this.#buffer.sampleRate;
        const endSample = Math.floor((time / 1000) * sampleRate);
        const windowSizeSamples = Math.floor(sampleRate * (windowSize / 1000));
        const startSample = Math.max(0, endSample - windowSizeSamples);
        const channelData = this.#buffer.getChannelData(0);
        const clampedEndSample = Math.min(endSample, channelData.length);

        if (startSample >= channelData.length) {
            return 0;
        }

        // Calculate RMS and peak with recency weighting, where RMS is used as the "base", and peak
        // will make the loudness respond more to the audio's actual loudness.
        let weightedSumSquares = 0;
        let totalWeight = 0;
        let peak = 0;

        const actualWindowSize = clampedEndSample - startSample;

        for (let i = startSample; i < clampedEndSample; i++) {
            const sample = channelData[i];

            // Calculate weight based on recency (samples closer to current time have higher weight)
            const distanceFromEnd = clampedEndSample - 1 - i;
            const normalizedDistance =
                actualWindowSize > 1 ? distanceFromEnd / (actualWindowSize - 1) : 0;
            const weight = Math.exp(-2 * normalizedDistance);

            weightedSumSquares += sample * sample * weight;
            totalWeight += weight;
            peak = Math.max(peak, Math.abs(sample));
        }

        const rms = totalWeight > 0 ? Math.sqrt(weightedSumSquares / totalWeight) : 0;
        const rawMix = Math.min((rms * loudnessPercent + peak * (1 - loudnessPercent)) * gain, 1);

        return rawMix;
    }

    /**
     * __playbackRegionGainNodes is only intended to be used by tests.
     * @internal
     */
    get __playbackRegionGainNodes() {
        return this.#playbackRegionGainNodes;
    }

    // ------------------------------------------------------------------------------------
    // TODO Because AudioAsset extends Base, it needs to implement the abstract methods
    //  However AudioAsset should be viewed & handled more like a BaseAsset because it's not
    //  part of luminary's core structure, but rather can be added or removed dynamically.
    //  The following are no-op implementations to satisfy the typings, but eventually
    //  should be refactored.
    // ------------------------------------------------------------------------------------

    _propagateBuffering(): never {
        throw new Error('should not be used');
    }

    override get areAssetsDoneTryingToLoad(): never {
        throw new Error('should not be used');
    }
}
